import { useEffect, useState, type ReactNode } from "react";
import { rewardsErrorMessageKey } from "@ticketon/rewards-graphql";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from "ui";
import type { ButtonProps } from "ui";
import type { Translator } from "../types/nav";
import { useRewardsAdmin } from "../host/RewardsAdminHost";

export interface IConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  /** Confirm button label; defaults to a translated "confirm". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Variant for the confirm button (e.g. "destructive" for dangerous actions). */
  confirmVariant?: ButtonProps["variant"];
  /**
   * Called when the user confirms. May be async: the dialog shows a spinner,
   * closes itself when it resolves and stays open showing the translated error
   * when it rejects.
   */
  onConfirm: () => void | Promise<void>;
  /** Extra content rendered between the description and the footer (e.g. a reason input). */
  children?: ReactNode;
  /** Disables the confirm button (e.g. when a required reason is empty). */
  confirmDisabled?: boolean;
  t?: Translator;
}

/** A confirmation modal used before destructive / state-changing actions. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmVariant = "default",
  onConfirm,
  children,
  confirmDisabled = false,
  t: tOverride,
}: IConfirmDialogProps) {
  const { t } = useRewardsAdmin({ t: tOverride });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) setErrorKey(null);
  }, [open]);

  const handleConfirm = async () => {
    setErrorKey(null);
    setIsSubmitting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      setErrorKey(rewardsErrorMessageKey(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        {errorKey && (
          <p
            className="rounded-lg border border-ticketon-error/20 bg-ticketon-error-bg px-3 py-2 text-body-sm text-ticketon-error-text"
            role="alert"
            data-testid="confirm-error"
          >
            {t(errorKey)}
          </p>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            data-testid="confirm-cancel"
          >
            {cancelLabel ?? t("rewards.action.cancel")}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={handleConfirm}
            disabled={isSubmitting || confirmDisabled}
            data-testid="confirm-accept"
          >
            {isSubmitting && <Spinner className="mr-2 h-4 w-4" />}
            {confirmLabel ?? t("rewards.action.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
